import { useCallback, useState } from 'react';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export type WinRateByMonthRow = {
  monthKey: string;
  label: string;
  winRate: number;
  won: number;
  lost: number;
  closed: number;
  isCurrentMonth: boolean;
};

function monthKeyInTimezone(iso: string, timezone: string) {
  return format(toZonedTime(new Date(iso), timezone), 'yyyy-MM');
}

/**
 * Win rate per month — won / (won + lost) for deals closed (marked Won/Lost) in
 * that month, January through the current month (business timezone). Matches the
 * "Win rate" KPI; the YTD headline is the pooled rate across all closed deals.
 */
export function useWinRateByMonthYtdBreakdown() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [months, setMonths] = useState<WinRateByMonthRow[]>([]);
  const [yearWinRate, setYearWinRate] = useState(0);
  const [yearWon, setYearWon] = useState(0);
  const [yearLost, setYearLost] = useState(0);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonths([]);
      setYearWinRate(0);
      setYearWon(0);
      setYearLost(0);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const zonedNow = toZonedTime(new Date(), timezone);
      const yearStartZoned = startOfYear(zonedNow);
      const currentMonthZoned = startOfMonth(zonedNow);

      setYearLabel(format(zonedNow, 'yyyy'));

      const rangeStart = fromZonedTime(yearStartZoned, timezone).toISOString();
      const rangeEnd = fromZonedTime(
        startOfMonth(addMonths(zonedNow, 1)),
        timezone
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('status, updated_at')
        .eq('business_id', businessId)
        .in('status', ['Won', 'Lost'])
        .gte('updated_at', rangeStart)
        .lt('updated_at', rangeEnd);

      if (queryError) throw queryError;

      const wonByMonth = new Map<string, number>();
      const lostByMonth = new Map<string, number>();
      let totalWon = 0;
      let totalLost = 0;

      for (const deal of data ?? []) {
        if (!deal.updated_at) continue;
        const key = monthKeyInTimezone(deal.updated_at, timezone);
        if (deal.status === 'Won') {
          wonByMonth.set(key, (wonByMonth.get(key) ?? 0) + 1);
          totalWon += 1;
        } else if (deal.status === 'Lost') {
          lostByMonth.set(key, (lostByMonth.get(key) ?? 0) + 1);
          totalLost += 1;
        }
      }

      const monthDates = eachMonthOfInterval({
        start: yearStartZoned,
        end: currentMonthZoned,
      });

      const rows: WinRateByMonthRow[] = monthDates.map((month) => {
        const key = format(month, 'yyyy-MM');
        const won = wonByMonth.get(key) ?? 0;
        const lost = lostByMonth.get(key) ?? 0;
        const closed = won + lost;
        return {
          monthKey: key,
          label: format(month, 'MMMM yyyy'),
          winRate: closed > 0 ? (won / closed) * 100 : 0,
          won,
          lost,
          closed,
          isCurrentMonth: isSameMonth(month, zonedNow),
        };
      });

      const totalClosed = totalWon + totalLost;
      setMonths(rows);
      setYearWon(totalWon);
      setYearLost(totalLost);
      setYearWinRate(totalClosed > 0 ? (totalWon / totalClosed) * 100 : 0);
    } catch (err) {
      console.error('Error loading win rate by month YTD breakdown:', err);
      setMonths([]);
      setYearWinRate(0);
      setYearWon(0);
      setYearLost(0);
      setError('Could not load win rate breakdown.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { months, yearWinRate, yearWon, yearLost, yearLabel, loading, error, load };
}
